import React, { useRef, useState, useEffect } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

/**
 * AccordionSection — standard collapsible section for the HR Portal.
 *
 * Header layout: [toggle: chevron + icon + title] … [trailing]
 * Trailing is outside the toggle control so it may include interactive
 * actions (Add CTAs, icon buttons) without nested-button HTML or
 * accidental expand/collapse on click.
 *
 * @param {boolean}   expanded        Whether the section is open
 * @param {Function}  onToggle        Called when the header is clicked
 * @param {'admin'|'employee'} theme  Controls focus-ring color (red / blue)
 * @param {'flush'|'padded'} contentVariant
 *   - "flush" (default): children render edge-to-edge with a border-t separator.
 *     Use for tables, lists, and anything that should touch the accordion edges.
 *   - "padded": children get px-4 pb-4 padding.
 *     Use for card grids, forms, and custom content that needs breathing room.
 * @param {boolean}  overflowVisible  When true, removes overflow clipping so that
 *   absolutely-positioned children (e.g. tooltips) can escape the accordion
 *   boundary. Use only on accordions that contain hover tooltips near the edges.
 * @param {boolean}  sticky           When true, the header sticks to the top of
 *   the nearest scrolling ancestor while the section is in view. Useful for long
 *   accordion content so the user always knows which section they are scrolling
 *   through.
 * @param {string}   stickyTopClassName Tailwind top-inset class for the sticky
 *   wrapper. Keep the default for nested scrollers. Page-level accordions inside
 *   the padded app-shell scroller use "-top-4 md:-top-6" so previous rows cannot
 *   remain visible above the frozen context.
 * @param {React.ReactNode} stickyExtra  Extra content (e.g. column headers) that
 *   sticks together with the header when sticky is true. Only rendered when the
 *   section is expanded.
 * @param {React.ReactNode} icon      Optional icon element (e.g. <Calendar />)
 * @param {string}   iconBg           Tailwind bg class for the icon circle (e.g. "bg-red-100")
 * @param {string|React.ReactNode} title  Section heading
 * @param {React.ReactNode} trailing  Optional right-side content (badges, counts, CTAs)
 * @param {string}   className        Extra classes on the outer container
 * @param {React.ReactNode} children  Collapsible body content
 */
export default function AccordionSection({
  expanded,
  onToggle,
  theme = 'admin',
  contentVariant = 'flush',
  overflowVisible = false,
  sticky = false,
  stickyTopClassName = 'top-0',
  stickyExtra,
  icon,
  iconBg,
  title,
  trailing,
  className = '',
  children,
}) {
  const sentinelRef = useRef(null);
  const [isStuck, setIsStuck] = useState(false);

  useEffect(() => {
    if (!sticky) return;
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsStuck(!entry.isIntersecting && entry.boundingClientRect.top < 0);
      },
      { threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [sticky]);

  const toggleThemeClass = theme === 'employee'
    ? 'accordion-section-toggle-employee'
    : 'accordion-section-toggle-admin';

  const headerRow = (
    <div className="accordion-section-header">
      <button
        type="button"
        onClick={onToggle}
        className={`accordion-section-toggle ${toggleThemeClass}`}
        aria-expanded={expanded}
      >
        {expanded
          ? <ChevronUp className="w-4 h-4 text-gray-500 flex-shrink-0" />
          : <ChevronDown className="w-4 h-4 text-gray-500 flex-shrink-0" />
        }
        {icon && (
          <div className={`accordion-section-icon ${iconBg || ''}`}>
            {icon}
          </div>
        )}
        <div className="accordion-section-title min-w-0">{title}</div>
      </button>
      {trailing && (
        <div className="flex flex-wrap items-center justify-end gap-2 flex-shrink-0 pl-2">
          {trailing}
        </div>
      )}
    </div>
  );

  return (
    <div className={`accordion-section ${overflowVisible ? '' : 'overflow-clip'} ${className}`}>
      {sticky && <div ref={sentinelRef} className="h-0" aria-hidden="true" />}
      {sticky ? (
        <div className={`sticky ${stickyTopClassName} z-20 bg-white transition-shadow${isStuck ? ' shadow-md' : ' rounded-t-2xl'}`}>
          {headerRow}
          {expanded && stickyExtra}
        </div>
      ) : (
        headerRow
      )}
      {expanded && (
        contentVariant === 'padded'
          ? <div className="accordion-section-content-padded">{children}</div>
          : children
      )}
    </div>
  );
}
